import { ianaTimezoneToHumanReadable } from "@bigbinary/neeto-time-zones";
import i18next from "i18next";
import { findIndexBy, isNotPresent } from "neetocist";
import { dayjs } from "neetocommons/utils";
import { timeFormat } from "neetocommons/utils/datetime";
import { prop, assoc, fromPairs, map, pick } from "ramda";
import * as yup from "yup";

import { DAYS } from "./constants";
import dayjsExtended from "./dayjsExtended";
import { SLOT_TIME_FORMAT, SLOT_TIME_FORMAT_24H } from "./Form/constants";

yup.addMethod(yup.array, "ensureSlotsCantOverlap", function () {
  return this.test("ensureSlotsCantOverlap", function (periods) {
    const {
      exists: isOverlapping,
      range1,
      range2,
    } = checkForOverlapBetweenPeriods(periods);

    if (!isOverlapping) return true;

    const { firstIndex, secondIndex } = findIndicesOfOverlappingRangesInPeriods(
      { range1, range2, periods }
    );

    const firstError = this.createError({
      path: `${this.path}[${firstIndex}]`,
      message: i18next.t("neetoMolecules.schedule.errors.period.overlap", {
        start: timeFormat.time(range2.startDate),
        end: timeFormat.time(range2.endDate),
      }),
    });

    const secondError = this.createError({
      path: `${this.path}[${secondIndex}]`,
      message: i18next.t("neetoMolecules.schedule.errors.period.overlap", {
        start: timeFormat.time(range1.startDate),
        end: timeFormat.time(range1.endDate),
      }),
    });

    return new yup.ValidationError([firstError, secondError]);
  });
});

const isFromAndToSame = (startTime, endTime) => {
  if (!startTime || !endTime) return true;

  return dayjs(startTime, SLOT_TIME_FORMAT_24H).isSame(
    dayjs(endTime, SLOT_TIME_FORMAT_24H)
  );
};

export const sortDays = chosenIndex => {
  if (chosenIndex < 0 || chosenIndex >= 7) return DAYS;

  return [...DAYS.slice(chosenIndex), ...DAYS.slice(0, chosenIndex)];
};

export const wdayValidation = yup.object().shape({
  periods: yup
    .array()
    .of(
      yup
        .object()
        .shape({
          startTime: yup
            .string()
            .required(
              i18next.t("neetoMolecules.schedule.errors.period.startTime")
            ),
          endTime: yup
            .string()
            .required(
              i18next.t("neetoMolecules.schedule.errors.period.endTime")
            )
            .test(
              "ensureToTimeNotBehindFromTime",
              i18next.t("neetoMolecules.schedule.errors.period.isBehind"),
              function (value) {
                const { startTime } = this.parent;
                if (isFromAndToSame(value, startTime)) return true;

                return isTimeBehind(startTime, value);
              }
            ),
        })
        .test(
          "ensureTimeNotSame",
          i18next.t("neetoMolecules.schedule.errors.period.sameTime"),
          value => {
            const { startTime, endTime } = value;

            return !isFromAndToSame(startTime, endTime);
          }
        )
    )
    .ensureSlotsCantOverlap(),
});

export const getScheduleValidationSchema = () =>
  yup.object().shape({
    wdays: yup
      .object()
      .shape(fromPairs(map(day => [day, wdayValidation], DAYS))),
  });

export const getUserTimeZone = () => window.globalProps?.user.timeZone;

export const getTimeZoneAndTime = () => {
  const timeZone = getUserTimeZone();
  const humanReadableTimezone = ianaTimezoneToHumanReadable(timeZone);

  // Requires timezone initialization
  // eslint-disable-next-line @bigbinary/neeto/use-standard-date-time-formats
  return `${humanReadableTimezone}: ${dayjs()
    .tz(timeZone)
    .format(SLOT_TIME_FORMAT)}`;
};

export const isTimeBehind = (timeA, timeB) =>
  dayjs(timeA, SLOT_TIME_FORMAT_24H).isBefore(
    dayjs(timeB, SLOT_TIME_FORMAT_24H)
  );

export const sortPeriodsByKey = ({ periods, key, order = "desc" }) =>
  periods.slice().sort((periodA, periodB) => {
    const isPeriodBehind = isTimeBehind(periodA[key], periodB[key]);
    if (order === "desc") return isPeriodBehind ? 1 : -1;

    return isPeriodBehind ? -1 : 1;
  });

/**
 * Check for overlapping between entered periods
 * @typedef {Object} Slots
 * @property {Array<{startTime:string, endTime: string}>} periods - startTime and endTime
 * @param {Slots} periods - changed time periods
 * @return {{exists: boolean, range1: DayjsRange | null, range2:DayjsRange | null }} - overlapped ranges if exists
 */
export const checkForOverlapBetweenPeriods = periods => {
  let response = { exists: false, range1: null, range2: null };

  if (!periods?.length) return response;

  const slotsSortedByStartTime = sortPeriodsByKey({
    periods,
    key: "startTime",
  });
  for (const [index, value] of slotsSortedByStartTime.slice(0, -1).entries()) {
    const seg1 = value;
    const seg2 = slotsSortedByStartTime[index + 1];
    const range1 = dayjsExtended.range(
      dayjsExtended(seg1.startTime, SLOT_TIME_FORMAT_24H),
      dayjsExtended(seg1.endTime, SLOT_TIME_FORMAT_24H)
    );

    const range2 = dayjsExtended.range(
      dayjsExtended(seg2.startTime, SLOT_TIME_FORMAT_24H),
      dayjsExtended(seg2.endTime, SLOT_TIME_FORMAT_24H)
    );
    if (range1.isOverlap(range2)) {
      response = { exists: true, range1, range2 };
      break;
    }
  }

  return response;
};

export const findIndicesOfOverlappingRangesInPeriods = ({
  range1,
  range2,
  periods,
}) => {
  const firstIndex = findIndexBy(
    { startTime: range1.startDate.format(SLOT_TIME_FORMAT_24H) },
    periods
  );

  const secondIndex = periods.findIndex(
    (slot, index) =>
      index > firstIndex &&
      slot.startTime === range2.startDate.format(SLOT_TIME_FORMAT_24H)
  );

  return { firstIndex, secondIndex };
};

export const checkIfTwoTimeZonesAreSame = (timeZone1, timeZone2) =>
  // Requires timezone initialization
  // eslint-disable-next-line @bigbinary/neeto/use-standard-date-time-formats
  dayjs().tz(timeZone1).format("Z") === dayjs().tz(timeZone2).format("Z");

export const handleCopy = ({ values, day, setFieldValue, wdaysToCopy }) => {
  const newPeriods = values.wdays[day].periods?.map(
    pick(["startTime", "endTime"])
  );

  if (isNotPresent(newPeriods)) return;

  wdaysToCopy.forEach(wday =>
    setFieldValue(`wdays.${wday}`, {
      available: true,
      periods: newPeriods.map(assoc("wday", wday)),
    })
  );
};

export const buildFormValuesArray = ({ firstDay, periods }) =>
  sortDays(firstDay).map(wday => ({
    periods: sortPeriodsByKey({
      periods: periods[wday],
      key: "startTime",
      order: "asc",
    }),
    available: !!periods[wday]?.length,
    wday,
  }));

export const buildFormValuesObject = ({ periods }) => {
  const formValues = {};

  DAYS.forEach(wday => {
    formValues[wday] = {
      periods: sortPeriodsByKey({
        periods: periods[wday],
        key: "startTime",
        order: "asc",
      }),
      available: !!periods[wday]?.length,
      wday,
    };
  });

  return formValues;
};

export const processFormValues = (values, firstDay) => ({
  wdays: buildFormValuesArray({
    firstDay,
    periods: map(prop("periods"), values.wdays),
  }),
});

export const convert24hTo12h = time =>
  // eslint-disable-next-line @bigbinary/neeto/use-standard-date-time-formats
  dayjs(time, SLOT_TIME_FORMAT_24H).format(SLOT_TIME_FORMAT);
